// v1.20.2 — Carte personnalité avec :
// 1) detectPersonalityIntent : restrictivement, on n'affiche une carte
//    QUE si l'utilisateur a explicitement posé une question type "qui est X" /
//    "parle moi de X" / "c est qui X" / "bio de X". Plus de faux positifs.
// 2) parseProfileCardMarker : extraction du tag [[PROFILE_CARD: nom="..."]]
//    émis par le LLM (utilisé en fallback Tavily quand la personne n'est pas
//    dans personalities_db.json). Permet au LLM de générer dynamiquement
//    une carte enrichie pour n'importe quelle personnalité.

import { useEffect, useState } from "react";

let _cache = null;
let _cacheTs = 0;
const TTL = 60 * 1000;

async function fetchPersonalities() {
  if (_cache && (Date.now() - _cacheTs) < TTL) return _cache;
  try {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const res = await fetch(`${base}/api/personalities/public`);
    const data = await res.json();
    _cache = data.personalities || [];
    _cacheTs = Date.now();
  } catch (_) {
    _cache = [];
  }
  return _cache;
}

function _norm(s) {
  return (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

// v1.20.2 — Détecte si l'utilisateur cherche EXPLICITEMENT une bio.
// Empêche les faux positifs : taper "Ouattara a fait quoi en 2020 ?"
// ne déclenchera plus une fiche carte.
const _INTENT_PATTERNS = [
  /\bqui\s+(est|etait|était)\b/i,
  /\bc['e ]?est\s+qui\b/i,
  /\bparle[\s-]?moi\s+de\b/i,
  /\bbiograph(ie|ique)\b/i,
  /\bportrait\s+(de|du)\b/i,
  /\bfich(e|er)\s+(de|sur)\b/i,
  /\bprofil\s+(de|du)\b/i,
  /\bpresent(e|er)[\s-]?moi\b/i,
  /\bdis[\s-]?moi\s+qui\b/i,
  /\bdécris[\s-]?moi\s+/i,
  /\bbio\s+(de|du)\b/i,
];
export function detectPersonalityIntent(text) {
  if (!text || typeof text !== "string") return false;
  return _INTENT_PATTERNS.some((re) => re.test(text));
}

// v1.20.2 — Parse [[PROFILE_CARD: nom="X" role="Y" pays="CI" annee_naissance="1942" annee_fin="présent" parti="RHDP" ethnie="Dioula"]]
// Le LLM peut émettre ce marker pour générer dynamiquement une carte
// (typiquement après une recherche Tavily quand la personne n'est pas dans la DB locale).
export function parseProfileCardMarker(text) {
  if (!text || typeof text !== "string") return null;
  const re = /\[\[PROFILE_CARD:\s*([^\]]+)\]\]/i;
  const m = text.match(re);
  if (!m) return null;
  const payload = m[1];
  const kv = {};
  // Capture les paires key="value" (espaces et accents OK dans value)
  const pairRe = /(\w+)\s*=\s*"([^"]*)"/g;
  let p;
  while ((p = pairRe.exec(payload)) !== null) {
    kv[p[1].toLowerCase()] = p[2].trim();
  }
  if (!kv.nom) return null;
  // Compose un objet "personality-like" compatible avec le composant card.
  const id = `llm_card_${_norm(kv.nom).replace(/\s+/g, "_")}`;
  const stats = [];
  if (kv.annee_naissance || kv.annee_fin) {
    const naiss = kv.annee_naissance || "?";
    const fin = kv.annee_fin || "présent";
    stats.push({ label: "Période", value: `${naiss} – ${fin}` });
  }
  if (kv.parti) stats.push({ label: "Parti/Domaine", value: kv.parti });
  return {
    id,
    nom: kv.nom,
    role: kv.role || "",
    pays: kv.pays || "CI",
    ethnie: kv.ethnie || "",
    parti: kv.parti || "",
    categorie: kv.categorie || "autre",
    stats,
    points_positifs: [],
    points_negatifs: [],
    photo_url: kv.photo_url || null,
    _from_llm: true,
  };
}

// v1.20.2 — userQuery est OBLIGATOIRE pour activer le matching DB.
// Sans intent explicite de l'utilisateur, on ne renvoie AUCUNE carte basée
// sur la DB. Le marker PROFILE_CARD du LLM, lui, reste toujours pris en compte.
export async function parseMessageWithCards(agentText, userQuery = "") {
  const out = { cards: [], cleanText: agentText };
  if (!agentText || typeof agentText !== "string") return out;

  // 1) Toujours essayer le marker LLM (peut court-circuiter la DB locale)
  const llmCard = parseProfileCardMarker(agentText);
  if (llmCard) {
    out.cards.push(llmCard);
    // On nettoie le marker du texte pour ne pas le voir dans la bulle.
    out.cleanText = agentText.replace(/\[\[PROFILE_CARD:[^\]]+\]\]\s*/i, "");
  }

  // 2) Matching DB UNIQUEMENT si l'utilisateur a explicitement demandé une perso.
  if (detectPersonalityIntent(userQuery)) {
    const all = await fetchPersonalities();
    if (all.length) {
      const lowerUser = _norm(userQuery);
      const lowerAgent = _norm(agentText);
      for (const p of all) {
        if (p.enabled === false) continue;
        // Skip si déjà ajouté via marker LLM
        if (out.cards.find((c) => _norm(c.nom) === _norm(p.nom))) continue;
        const candidates = [p.nom, ...(p.nom_variations || [])]
          .map(_norm)
          .filter((c) => c.length >= 3);
        // Le nom doit apparaître DANS LA QUESTION UTILISATEUR (priorité)
        // OU dans la réponse agent si l'intent est confirmé.
        const inUser = candidates.some((c) => lowerUser.includes(c));
        const inAgent = candidates.some((c) => lowerAgent.includes(c));
        if (inUser || inAgent) out.cards.push(p);
      }
    }
  }
  // 2 cards max pour pas surcharger.
  out.cards = out.cards.slice(0, 2);
  return out;
}

import FavoriteStarButton from "./FavoriteStarButton";

export default function PersonalityCard({ data, ask }) {
  if (!data) return null;
  const initials = data.nom.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
  const handleAsk = (text) => {
    if (typeof ask === "function") ask(text);
  };
  return (
    <div className="civ-personality-card" data-testid={`personality-card-${data.id}`} style={{ position: "relative" }}>
      <FavoriteStarButton
        itemType="personality"
        itemId={data.id}
        itemLabel={data.nom}
        itemMeta={{ role: data.role, photo_url: data.photo_url }}
      />
      <div className="civ-pc-header">
        {data.photo_url ? (
          <img src={data.photo_url} alt={data.nom} className="civ-pc-photo" loading="lazy" />
        ) : (
          <div className="civ-pc-initials" aria-hidden="true">{initials}</div>
        )}
        <div className="civ-pc-name-block">
          <h3 className="civ-pc-name">{data.nom}</h3>
          {data.role && <p className="civ-pc-role">{data.role}</p>}
          {data.surnom && <p className="civ-pc-surnom">« {data.surnom} »</p>}
        </div>
        {data.categorie && (
          <span className={`civ-pc-cat civ-pc-cat-${data.categorie}`}>{data.categorie}</span>
        )}
        {data._from_llm && (
          <span
            className="civ-pc-cat civ-pc-cat-autre"
            title="Fiche enrichie en direct par recherche web"
            style={{ marginLeft: 6 }}
          >
            web ✨
          </span>
        )}
      </div>
      {data.stats?.length > 0 && (
        <div className="civ-pc-stats">
          {data.stats.map((s, i) => (
            <div key={i} className="civ-pc-stat">
              <span className="civ-pc-stat-label">{s.label}</span>
              <span className="civ-pc-stat-value">{s.value}</span>
            </div>
          ))}
        </div>
      )}
      {data.points_positifs?.length > 0 && (
        <div className="civ-pc-section civ-pc-positifs">
          <h4>✅ Points forts</h4>
          <ul>{data.points_positifs.map((p, i) => <li key={i}>{p}</li>)}</ul>
        </div>
      )}
      {data.points_negatifs?.length > 0 && (
        <div className="civ-pc-section civ-pc-negatifs">
          <h4>⚠️ Points controversés</h4>
          <ul>{data.points_negatifs.map((p, i) => <li key={i}>{p}</li>)}</ul>
        </div>
      )}
      <div className="civ-pc-actions" data-testid={`personality-actions-${data.id}`}>
        <button type="button" className="civ-pc-action-btn" onClick={() => handleAsk(`Donne moi la biographie complète de ${data.nom}`)} data-testid={`personality-action-bio-${data.id}`}>
          Biographie ↗
        </button>
        <button type="button" className="civ-pc-action-btn" onClick={() => handleAsk(`Quel est le bilan complet de ${data.nom} ?`)} data-testid={`personality-action-bilan-${data.id}`}>
          Bilan ↗
        </button>
        <button type="button" className="civ-pc-action-btn" onClick={() => handleAsk(`Quel est l'héritage de ${data.nom} en Côte d'Ivoire ?`)} data-testid={`personality-action-heritage-${data.id}`}>
          Héritage ↗
        </button>
      </div>
      <div className="civ-pc-footer">
        {data.parti && <span>🏛 {data.parti}</span>}
        {data.ethnie && <span>👥 {data.ethnie}</span>}
        {data.formation && <span>🎓 {data.formation}</span>}
      </div>
    </div>
  );
}

export function PersonalityCardBlock({ text, ask, userQuery }) {
  const [cards, setCards] = useState([]);
  useEffect(() => {
    let cancelled = false;
    parseMessageWithCards(text, userQuery).then((r) => {
      if (!cancelled) setCards(r.cards);
    });
    return () => { cancelled = true; };
  }, [text, userQuery]);
  if (!cards.length) return null;
  return (
    <div className="civ-personality-cards-wrap" data-testid="personality-cards-wrap">
      {cards.map((c) => <PersonalityCard key={c.id} data={c} ask={ask} />)}
    </div>
  );
}
